import type {
  ConfigRequestBody,
  ResponseError,
  UpdateConfigurationRulesRequest,
} from '@rtkwlf/rendall';
import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';

import { useOrganizationContext } from '@packages/legacy/organization-context';
import { ticketKeys } from '@packages/utils/queryKeyFactories/tickets';

import { useRendallApi } from '../use-rendall-api';

import { getAlertConfigurationRulesQueryKey } from './use-alert-configuration-rules';

export type UpdateConfigurationRules = Omit<
  UpdateConfigurationRulesRequest,
  'organizationId'
>;

export const useUpdateConfigurationRules = (
  options?: Omit<
    UseMutationOptions<
      ConfigRequestBody,
      ResponseError,
      UpdateConfigurationRules
    >,
    'mutationFn'
  > & { ticketId?: string },
) => {
  const api = useRendallApi();
  const { selectedOrganization } = useOrganizationContext();
  const queryClient = useQueryClient();
  const { onSuccess, ticketId, ...otherOptions } = options ?? {};

  return useMutation({
    mutationFn: ({
      alertId,
      configRequestBody: body,
    }: UpdateConfigurationRules) => {
      const configRequestBody = ticketId ? { ...body, ticketId } : body;
      return api.updateConfigurationRules({
        organizationId: selectedOrganization.id,
        alertId,
        configRequestBody,
      });
    },
    onSuccess: (...args) => {
      const alertConfigRulesQueryKey = getAlertConfigurationRulesQueryKey(
        selectedOrganization.id,
      );

      queryClient.invalidateQueries({ queryKey: alertConfigRulesQueryKey });
      if (ticketId) {
        queryClient.invalidateQueries({
          queryKey: ticketKeys.detail(selectedOrganization.id, ticketId),
        });
      }

      if (onSuccess) {
        onSuccess(...args);
      }
    },
    ...otherOptions,
  });
};
